import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { useAppSelector } from 'setup/store';
import { IDateFilter, IPageData, SnackbarType } from 'common/interfaces';
import UITable, { DisplayItem } from 'components/lib/UI/table/UITable';
import { IStore } from 'common/interfaces/istore';
import { useNavigate, useParams } from 'react-router-dom';
import { useRoyaltyReportActions } from 'actions/royalty-report-actions';
import { IACHFormResponse } from 'common/interfaces/iRoyaltyReports';
import AchInputForm, { ACH_FORM_FIELDS } from 'components/ach-input-form/AchInputForm';
import useGlobalStyles from 'assets/styles/global-styles';
import { getDisplayDate } from 'common/utils/date-util';
import { formatCurrency } from 'common/utils/string-utils';
import { useAppActions } from 'actions/app-actions';
import UICard from 'components/lib/card/UICard';
import { useSessionActions } from 'actions/session-actions';

const DEFAULT_HEADERS = ACH_FORM_FIELDS.map((item) => ({ label: item.label })) as DisplayItem[];

const RoyaltyFormResponsesPage = () => {
  const { t } = useTranslation();
  const params = useParams();
  const reportId = params.reportId ? Number(params.reportId) : undefined;
  const appConfig = useAppSelector((state) => state.appReducer.appConfig);
  const navigate = useNavigate();
  const error = useAppSelector((state) => state.royaltyReportReducer.error);
  const isLoading = useAppSelector((state) => state.royaltyReportReducer.loading);
  const ongoingRoyaltyReport = useAppSelector((state) => state.royaltyReportReducer.ongoingRoyaltyReport);
  const selectedFormResponses = useAppSelector((state) => state.royaltyReportReducer.selectedFormResponses);
  const [_selectedStore, setSelectedStore] = useState<IStore>();
  const [_selectedDateFilter, setSelectedDateFilter] = useState<IDateFilter>();

  const globalStyles = useGlobalStyles();

  // const { setSelectedStore } = useSessionActions();
  const { showSnackBar } = useAppActions();
  const { loadRoyaltyConfigs } = useSessionActions();
  const { getRoyaltyReportDetails, resetContext, resetUpdatedData, approveRoyaltyReport } = useRoyaltyReportActions();

  const _loadFormResponses = async (reportId: number) => {
    await getRoyaltyReportDetails(reportId);
  };

  useEffect(() => {
    resetUpdatedData();
    if (reportId) {
      loadRoyaltyConfigs();
      _loadFormResponses(reportId);
    }
  }, [reportId]);

  function _fetchMoreData(pageNo: number, pageSize: number): Promise<IPageData<IACHFormResponse> | undefined> {
    console.log('_fetchMoreData:::', pageNo, pageSize);
    return Promise.resolve(undefined);
  }

  const _onSearchReport = async (dateFilter: IDateFilter, store: IStore | undefined | null) => {};

  const keyExtractor = (item: IACHFormResponse, index: number) => {
    return (item.id || index).toString();
  };

  const _handleRowSelection = (item: IACHFormResponse) => {};

  const _handleApproveRoyaltyReport = async () => {
    if (ongoingRoyaltyReport?.id) {
      const success = await approveRoyaltyReport(ongoingRoyaltyReport.id);
      if (success) {
        showSnackBar(SnackbarType.SUCCESS, t('success.approved'));
        navigate('/royalty');
      }
    }
  };

  const _handleCancel = () => {
    navigate('/royalty');
  };

  const _renderRow = (item: IACHFormResponse, index: number) => {
    return (
      <>
        <td
          // style={{ fontWeight: item.isBold ? 'bold' : '300', flex: item.flex || 0 }}
          key={'td:' + index + ':' + item.storeId}
          style={{ padding: 30 }}
        >
          <Typography>{item.storeName}</Typography>
        </td>
        <td
          // style={{ fontWeight: item.isBold ? 'bold' : '300', flex: item.flex || 0 }}
          key={'td:' + index + ':' + 2}
        >
          <AchInputForm formData={item} />
        </td>
      </>
    );
  };
  return (
    <section>
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} className="pageTitleTitleContainer">
        <h2 className="pageTitle">{t('page.achFormResponses')}</h2>
        {/* <span>{selectedFormResponses?.totalItems}</span> */}
      </Box>

      <Paper
        style={{
          padding: '1rem 2rem',
          margin: '1rem 0rem',
        }}
      >
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} style={{ padding: '10px 0px' }}>
          <Typography variant="body1">
            <span style={globalStyles.deactivatedText}>Status:</span> {ongoingRoyaltyReport?.status}
          </Typography>
          <Typography variant="body1">
            <span style={globalStyles.deactivatedText}>Week Ending Date: </span>
            {getDisplayDate(ongoingRoyaltyReport?.reportDate)}
          </Typography>
          <Typography variant="body1">
            <span style={globalStyles.deactivatedText}>Total Amount: </span>
            {formatCurrency(ongoingRoyaltyReport?.totalAmount || 0, 'currency', 2)}
          </Typography>
        </Box>
      </Paper>

      <div style={{ margin: '0px 0px', padding: '0px' }}>
        {/* {isLoading ? <LoadingSpinner /> : null} */}
        {selectedFormResponses && selectedFormResponses.totalItems > 0 ? (
          <UICard title="">
            <UITable<IACHFormResponse>
              pagedData={selectedFormResponses}
              keyExtractor={keyExtractor}
              pageSize={appConfig.defaultListSize}
              loadData={_fetchMoreData}
              renderRowItem={_renderRow}
              headers={undefined}
              isLoading={isLoading}
              onItemPressed={_handleRowSelection}
              title={_selectedStore?.storeNumber ? 'Reports for Store: ' + _selectedStore?.storeNumber : ''}
              hideActions
              stickyHeader
            />

            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} style={{ padding: '10px 0px' }}>
              {ongoingRoyaltyReport?.status !== 'APPROVED' ? (
                <Button
                  variant="contained"
                  disabled={isLoading}
                  color="primary"
                  onClick={() => _handleApproveRoyaltyReport()}
                >
                  Approve
                </Button>
              ) : null}
              <Button disabled={isLoading} variant="contained" onClick={_handleCancel} color="error">
                Back
              </Button>
            </Box>
          </UICard>
        ) : selectedFormResponses?.totalItems === 0 ? (
          <Alert severity="warning">No Report found</Alert>
        ) : !isLoading ? (
          <Alert severity="info">Try refreshing the page.</Alert>
        ) : null}
      </div>
    </section>
  );
};

export default RoyaltyFormResponsesPage;
